import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartOutline } from '@fortawesome/free-regular-svg-icons';
import { SCREENS, SCREENS_MAX } from '../../../../../constants';
import { URLS } from '../../../../../constants/urls';
import { useNavigate } from 'react-router-dom';
import { ICalculatedPriceInfo } from '../../../../../__generated__/graphql';
import { Colors } from '../../../../styles/colors';
import { perMonthEquivalent } from '../../../../helpers/monthlyTrip';
import { useBrandLocale } from '../../../../hooks/useBrandLocale';
import { convertDistance, formatDistance } from '../../../../utils/units';

const CardContainer = styled.div`
  ${tw`
    box-border
    flex
    flex-col
    md:flex-row
    bg-white
    overflow-hidden
    cursor-pointer
    transition-all
    duration-200
    p-1
    mb-6
  `}
  border: 1px solid ${Colors.BorderLight};
  border-radius: 12px;

  @media (max-width: ${SCREENS_MAX.md}) {
    height: auto;
    padding: 8px;
    border-radius: 16px;
  }

  @media (min-width: ${SCREENS.md}) {
    height: calc(200px / 1.4 * 1.2);
    align-items: stretch;
  }

  &:hover {
    border-color: ${Colors.Obsidian};
    transform: scale(1.01);
  }
`;

const ImageContainer = styled.div`
  ${tw`
    relative
    w-full
    box-border
  `}
  height: calc(200px / 1.4);

  @media (max-width: ${SCREENS_MAX.md}) {
    height: auto;
    aspect-ratio: 16 / 10;
    border-radius: 12px;
    overflow: hidden;
  }

  @media (min-width: ${SCREENS.md}) {
    height: auto;
    width: 42%;
    flex: 0 0 42%;
  }
`;

const CarImage = styled.img`
  ${tw`
    w-full
    h-full
    object-cover
    box-border
  `}
`;

const ContentContainer = styled.div`
  ${tw`
    w-full
    px-3
    py-2
    flex
    flex-col
    box-border
    justify-center
  `}

  @media (max-width: ${SCREENS_MAX.md}) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 12px;
    row-gap: 2px;
    align-items: end;
    padding: 10px 4px 2px;
  }
`;

const Header = styled.div`
  ${tw`
    flex
    flex-col
    gap-1
 w-full max-w-full min-w-0 box-border`}

  @media (max-width: ${SCREENS_MAX.md}) {
    grid-column: 1;
    grid-row: 1;
  }

  @media (min-width: ${SCREENS.md}) {
    ${tw`
      flex-row
      justify-between
      items-start
    `}
  }
`;

const Title = styled.h3`
  ${tw`
    text-base
    font-bold
    leading-tight
  `}

  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`text-lg`}
  }
`;

const Rating = styled.div`
  ${tw`
    flex
    items-center
    gap-1
    text-sm
  `}
`;

const Location = styled.p`
  ${tw`
    text-xs
    text-text-secondary
    mt-0.5
    leading-tight
  `}

  @media (max-width: ${SCREENS_MAX.md}) {
    grid-column: 1;
    grid-row: 2;
    margin-top: 2px;
  }
`;

const Price = styled.div`
  ${tw`
    mt-auto
    text-right
  `}

  @media (max-width: ${SCREENS_MAX.md}) {
    grid-column: 2;
    grid-row: 1 / span 2;
    margin-top: 0;
    align-self: end;
  }
`;

const PriceInfo = styled.div`
  ${tw`
    text-sm
    italic
    text-text-secondary
    flex
    flex-col
    gap-1
  `}

  @media (max-width: ${SCREENS_MAX.md}) {
    font-style: normal;
  }
`;

const PriceAmount = styled.span`
  ${tw`
    font-bold
    text-base
    leading-tight
  `}
  color: ${Colors.Champagne};

  @media (max-width: ${SCREENS_MAX.md}) {
    color: var(--color-text-primary);
    font-size: 1.05rem;
  }
`;

const PriceLabel = styled.div`
  ${tw`
    text-xs
    text-text-secondary
    leading-tight
  `}
`;

const FavoriteButton = styled.button<{ $isFavorite?: boolean }>`
  ${tw`
    absolute
    top-2
    right-2
    bg-white
    border-none
    z-10
    flex
    items-center
    justify-center
    cursor-pointer
  `}
  padding: 7px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  svg {
    color: ${props => (props.$isFavorite ? '#E11D48' : 'var(--color-text-secondary)')};
    font-size: 16px;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const Badge = styled.span`
  ${tw`
    absolute
    top-2
    left-2
    text-sm
    font-semibold
    px-2
    py-1
  `}
  background: ${Colors.Obsidian};
  color: white;
  border-radius: 4px;
`;

const DiscountAmount = styled.span`
  ${tw`
    text-sm
    text-red-500
    text-text-secondary
    line-through
    leading-tight
  `}
`;
const DatesNote = styled.div`
  ${tw`text-xs font-semibold self-start`}
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--color-bg-secondary);
  color: ${Colors.TextPrimary};
`;

interface ICarCardProps {
  id: string;
  name: string;
  thumbnailUrl: string;
  location: string;
  weekdayPrice: number;
  onFavoriteClick?: (e: React.MouseEvent) => void;
  isFavorite?: boolean;
  urlParams: string;
  calculatedPrice?: ICalculatedPriceInfo;
  createdAt?: number;
  showDeliveryFee?: boolean;
  isNew?: boolean;
  distanceMiles?: number;
  /** Spec 028 similar dates: "Nov 3 to Dec 3 · 2 days earlier"; the price is for these dates. */
  datesNote?: string;
}

export function CarCard({
  id,
  name,
  thumbnailUrl,
  location,
  onFavoriteClick,
  isFavorite = false,
  urlParams,
  calculatedPrice,
  createdAt,
  showDeliveryFee = false,
  isNew = false,
  distanceMiles,
  datesNote,
}: ICarCardProps) {
  // Spec 028: distances in this site's unit (the API answers in miles).
  const { distanceUnit } = useBrandLocale();
  const navigate = useNavigate();
  const totalPrice = calculatedPrice?.totalPriceWithDelivery ?? calculatedPrice?.totalPrice ?? 0;
  const deliveryFee = calculatedPrice?.deliveryFee;
  // Spec 028: a quote the server priced by the month also reads per month.
  const perMonth = perMonthEquivalent(calculatedPrice ?? {});

  const handleClick = () => {
    navigate(`/cars/guest/view/${id}?${urlParams}`);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onFavoriteClick) {
      onFavoriteClick(e);
    }
  };

  return (
    <CardContainer onClick={handleClick} data-testid={`search-car-card-${id}`}>
      <ImageContainer>
        {isNew && <Badge>New</Badge>}
        <CarImage
          src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${thumbnailUrl}`}
          alt={name}
          data-testid={`search-car-image-${id}`}
        />
        {onFavoriteClick && (
          <FavoriteButton
            $isFavorite={isFavorite}
            onClick={handleFavoriteClick}
            data-testid={`search-car-favorite-${id}`}
          >
            <FontAwesomeIcon icon={isFavorite ? faHeart : faHeartOutline} />
          </FavoriteButton>
        )}
      </ImageContainer>
      <ContentContainer>
        <Header>
          <Title data-testid={`search-car-title-${id}`}>{name}</Title>
          <Rating>{}</Rating>
        </Header>
        <Location data-testid={`search-car-location-${id}`}>
          {location}
          {distanceMiles !== undefined && distanceMiles > 0 && (
            <span
              style={{
                marginLeft: '8px',
                color: 'var(--color-text-secondary)',
                fontSize: '0.875rem',
              }}
            >
              •{' '}
              {formatDistance(
                convertDistance(distanceMiles, 'mi', distanceUnit),
                distanceUnit,
                'away',
              )}
            </span>
          )}
        </Location>
        {datesNote && (
          <DatesNote data-testid={`search-car-similar-dates-${id}`}>{datesNote}</DatesNote>
        )}
        <Price>
          <PriceInfo>
            <div>
              <PriceAmount data-testid={`search-car-price-${id}`}>${totalPrice}</PriceAmount>{' '}
              <span>total</span>
              {perMonth !== null && (
                <PriceLabel data-testid={`search-car-per-month-${id}`}>
                  ≈ ${perMonth.toLocaleString()}/mo
                </PriceLabel>
              )}
              <PriceLabel>before taxes and fees</PriceLabel>
              {showDeliveryFee && deliveryFee !== undefined && deliveryFee !== null ? (
                <PriceLabel>Delivery fee: ${deliveryFee?.toFixed(2) ?? 0.0}</PriceLabel>
              ) : null}
              {calculatedPrice?.discountAmount ? (
                <div>
                  <span>Original Total:</span>{' '}
                  <DiscountAmount>
                    ${(totalPrice ?? 0) + (calculatedPrice?.discountAmount ?? 0)}{' '}
                  </DiscountAmount>
                </div>
              ) : null}
            </div>
          </PriceInfo>
        </Price>
      </ContentContainer>
    </CardContainer>
  );
}
